import * as Sentry from '@sentry/react';
import { keyBy } from 'lodash/fp';
import { RendererContextType } from '../../../../contexts/RendererContext';
import { ProjectShellFragment } from '../../../../types/graphqlTypes';
import { EventInput } from '../../../../workers/OrthophotoReaderWorker';
import { EventOutput } from '../../../../workers/OrthophotoReaderWorker';

type Project = ProjectShellFragment;
type OrthophotoLayer = Project['state']['orthophotoLayers'][number];

export function syncOrthophotoLayers({
  viewer,
  project,
  initializedPointclouds,
  orthophotoLayers,
  getGeoTiffData,
  transformGeoTiffCoordinates,
}: {
  viewer: NonNullable<RendererContextType['viewer']>;
  project: Project | undefined;
  initializedPointclouds: string[];
  orthophotoLayers: OrthophotoLayer[];
  getGeoTiffData: (input: EventInput['data']) => Promise<EventOutput>;
  transformGeoTiffCoordinates: (origin: number[], crs: number) => number[] | Promise<number[]>;
}): void {
  if (!project?.state.orthophotoLayers) return;
  if (!project.pointClouds.every((pointcloud) => initializedPointclouds.includes(pointcloud.id))) return;

  const sceneOrthophotos = viewer.scene.orthophotos;
  const sceneOrthophotosByIdentifier = keyBy('identifier', sceneOrthophotos);
  const databaseOrthophotosByIdentifier = keyBy('identifier', project.state.orthophotoLayers);

  orthophotoLayers.forEach((layer) => {
    const currentOrthophoto = sceneOrthophotosByIdentifier[layer.identifier];
    if (currentOrthophoto) {
      if (currentOrthophoto.visible === layer.visible) return;
      else currentOrthophoto.visible = layer.visible;
    } else if (layer.url) {
      const orthophoto = viewer.scene.addOrthophotoLayer(layer.identifier, layer.elevation || 0);
      getGeoTiffData({ url: layer.url }).then(async (geotiff) => {
        orthophoto.initialise({
          visible: layer.visible,
          ...geotiff,
          origin: await transformGeoTiffCoordinates(geotiff.origin, geotiff.crs),
          elevation: layer.elevation || 0,
        });
      }).catch((error: Error) => {
        console.error('Failed to load orthophoto layer', layer.identifier, error);
        Sentry.captureException(error, { tags: { worker: 'OrthophotoReaderWorker' } });
      });
    }
  });

  sceneOrthophotos.forEach((orthophoto) => {
    if (!databaseOrthophotosByIdentifier[orthophoto.identifier]) {
      viewer.scene.removeOrthophotoLayer({ identifier: orthophoto.identifier });
    }
  });
}
